import { api, useSessionStore } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import { deviceResourceSchema, messageResourceSchema, type DeviceResource } from './types';

/**
 * Het eigen account: apparaten en wachtwoord.
 *
 * Alles gaat met het **eigen** token van de sessie. Kijkt een platformbeheerder
 * mee, dan draagt de http-client het tijdelijke token van een ander - en een
 * wachtwoord wijzigen of een apparaat intrekken mag nooit per ongeluk bij die
 * ander terechtkomen.
 */
function asSelf() {
    const token = useSessionStore.getState().session?.token;

    return { cacheKey: false, ...(token ? { token } : {}) } as const;
}

export interface Device {
    uuid: string;
    name: string;
    /** "iPad", "Webbrowser": het label van de API. */
    platformLabel: string;
    /** Tablet of telefoon: blijft 14 dagen ingelogd, een computer 12 uur. */
    fieldDevice: boolean;
    lastSeenAt: string | null;
    lastSyncAt: string | null;
    trustedUntil: string | null;
    revokedAt: string | null;
    trusted: boolean;
    revoked: boolean;
    /** Het apparaat waarop je dit nu leest. */
    current: boolean;
}

export function toDevice(resource: DeviceResource): Device {
    return {
        uuid: resource.uuid,
        name: resource.name,
        platformLabel: resource.platform_label,
        fieldDevice: resource.is_field_device,
        lastSeenAt: resource.last_seen_at,
        lastSyncAt: resource.last_sync_at,
        trustedUntil: resource.trusted_until,
        revokedAt: resource.revoked_at,
        trusted: resource.is_trusted,
        revoked: resource.is_revoked,
        current: resource.is_current,
    };
}

export interface PasswordChange {
    currentPassword: string;
    password: string;
    passwordConfirmation: string;
}

export const accountApi = {
    /**
     * `GET /devices`: de eigen apparaten. Een eigenaar krijgt daar
     * ook de apparaten van collega's bij; die horen niet op deze pagina.
     */
    async devices(): Promise<Device[]> {
        const self = useSessionStore.getState().session?.user.uuid;

        return unwrapCollection(deviceResourceSchema, await api.get('/devices', asSelf()))
            .items.filter((device) => device.owner.uuid === self)
            .map(toDevice);
    },

    async renameDevice(deviceUuid: string, name: string): Promise<Device> {
        return toDevice(
            unwrap(
                deviceResourceSchema,
                await api.patch(`/devices/${deviceUuid}`, { name }, asSelf()),
            ),
        );
    },

    /**
     * Het token van het apparaat vervalt meteen: elk volgend verzoek ervan is een
     * 401 `device-unavailable` met `reason: revoked`, en inloggen lukt er niet meer
     * tot het weer is toegelaten.
     */
    async revokeDevice(deviceUuid: string): Promise<void> {
        await api.delete(`/devices/${deviceUuid}`, { ...asSelf(), expect: 'none' });
    },

    /**
     * `POST /devices/{device}/restore`: een ingetrokken apparaat mag weer inloggen.
     * De oude sessie komt niet terug; op dat apparaat log je opnieuw in.
     */
    async restoreDevice(deviceUuid: string): Promise<Device> {
        return toDevice(
            unwrap(
                deviceResourceSchema,
                await api.post(`/devices/${deviceUuid}/restore`, undefined, asSelf()),
            ),
        );
    },

    /**
     * `PATCH /auth/password`. Alle andere sessies vervallen, deze blijft. Geeft
     * de bevestiging van de API terug.
     */
    async changePassword(input: PasswordChange): Promise<string> {
        return unwrap(
            messageResourceSchema,
            await api.patch(
                '/auth/password',
                {
                    current_password: input.currentPassword,
                    password: input.password,
                    password_confirmation: input.passwordConfirmation,
                },
                asSelf(),
            ),
        ).message;
    },
};
